import { useEffect, useRef, useState, RefObject } from 'react'
import './style.css'
import Remote from '../remote'
import TouchableOpacity from '../../common/TouchableOpacity'
import Tree from '../../common/Tree'
import ChannelList from '../../common/ChannelList'
import { useAuth } from '../../provider/authentication'
import Input from '../../common/Input'
import { usePlayer } from '../../provider/playerSource'
import { SourceType } from '../../const/SourceType'
import VodChannel from './VodChannel'
import LogoutButton from './LogoutButton'
import TerminalIcon from '../../assets/icons/terminal'
import CaptureIcon from '../../assets/icons/capture'
import ChannelIcon from '../../assets/icons/channel'
import RemoteIcon from '../../assets/icons/remote'
import SettingsIcon from '../../assets/icons/settings'
import { useWebsocket } from '../../provider/websocket'
import { useScreenShare } from '../../provider/screenShare'
import Text from '../../common/Text'
import ListRow from '../../common/ListRow'
import ListRowAction from '../../common/ListRowAction'
import CopyIcon from '../../assets/icons/copy'
import DeleteIcon from '../../assets/icons/delete'
import StopIcon from '../../assets/icons/stop'
import EditIcon from '../../assets/icons/edit'
import InstanceViewModal, { InstanceViewHandle } from '../../common/Modals/instanceView'
import EditInstanceViewModal, { EditInstanceViewHandle } from '../../common/Modals/editInstanceView'
import GroupFilterViewModal, { GroupFilterViewHandle } from '../../common/Modals/groupFilterView'
import InstanceLogViewModal, { InstanceLogViewHandle } from '../../common/Modals/instanceLogView'
import {
  getActiveInstances,
  getInstancePresets,
  getHlsToken,
  getCaptureList,
  startInstance,
  stopInstance,
  play,
  searchChannels,
  CHANNEL_PAGE_SIZE,
  getChannelGroups,
  getPlaylistFilter,
  savePlaylistFilter,
  deleteCaptureItem,
  getVideoToken,
  getPlaybackSettings,
  updatePlaybackSettings,
  stopScreenShare,
} from '../../api/endpoints'
import type {
  ActiveInstance,
  CaptureItem,
  PlaybackSettings,
  Preset,
  PresetTreeNode,
  PlaylistChannel,
} from '@expostreamer/shared-types'
import {
  addPresetTreeNode,
  removePresetTreeNode,
  replacePresetTreeNode,
} from '../../utils/presetTree'
import styles from './style'

export interface DrawerHandle {
  handleDrawerState: (state: 'open' | 'closed') => void
}

interface DrawerProps {
  setInstanceList: (instances: ActiveInstance[]) => void
  instanceList: ActiveInstance[]
  open?: boolean
  ref?: RefObject<DrawerHandle | null>
}

const menuItems = {
  instanceControl: { id: 'instanceControl', icon: TerminalIcon },
  captureList: { id: 'captureList', icon: CaptureIcon },
  channelList: { id: 'channelList', icon: ChannelIcon },
  vodChannel: { id: 'vodChannel', icon: ChannelIcon },
  remote: { id: 'remote', icon: RemoteIcon },
  settings: { id: 'settings', icon: SettingsIcon },
}

const isIOS = /(iPad|iPhone|iPod)/g.test(navigator.userAgent)

function formatTime(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  const paddedMinutes = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes)
  const paddedSeconds = String(secs).padStart(2, '0')

  return hours > 0 ? `${hours}:${paddedMinutes}:${paddedSeconds}` : `${paddedMinutes}:${paddedSeconds}`
}

function Drawer({ setInstanceList, instanceList, ref }: DrawerProps) {
  const { accessToken } = useAuth()
  const { videoSource, setVideoSource } = usePlayer()
  const { webSocket, registerWebsocketMessaging, websocketOpen } = useWebsocket()
  const { activeShares } = useScreenShare()

  const [captureList, setCaptureList] = useState<CaptureItem[]>([])
  const [activeWindow, setActiveWindow] = useState<string>(menuItems.instanceControl.id)
  const [searchResults, setSearchResults] = useState<PlaylistChannel[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [hasMoreResults, setHasMoreResults] = useState(false)
  // Bumped on every new search so responses of an older search (or a page
  // still loading for it) are dropped instead of mixed into the new results.
  const searchIdRef = useRef(0)
  const loadingMoreRef = useRef(false)
  const [channelGroups, setChannelGroups] = useState<string[]>([])
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])
  const [hlsToken, setHlsToken] = useState<string | undefined>()

  const [showInstanceSelectModal, setShowInstanceSelectModal] = useState(false)
  const [instancePresets, setInstancePresets] = useState<Preset[]>([])
  const [playbackSettings, setPlaybackSettings] = useState<PlaybackSettings | null>(null)

  const currentSelectedParent = useRef<string | null>(null)
  const drawerRef = useRef<HTMLDivElement>(null)
  const [drawerPosition, setDrawerPosition] = useState(0)

  const [instanceMetadata, setInstanceMetadata] = useState<
    Record<string, { duration: string; startDate: string }>
  >({})
  const [progressBarValue, setProgressBarValue] = useState<number | string>(0)
  const [instanceLogs, setInstanceLogs] = useState<Record<string, string[]>>({})

  const modalInstanceViewRef = useRef<InstanceViewHandle>(null)
  const modalEditInstanceViewRef = useRef<EditInstanceViewHandle>(null)
  const modalGroupFilterViewRef = useRef<GroupFilterViewHandle>(null)
  const modalInstanceLogViewRef = useRef<InstanceLogViewHandle>(null)

  // Skips the debounced persist for the render that applies the group
  // selection loaded from disk (both the initial [] and the fetched value),
  // so only actual user edits get written back.
  const skipNextGroupsSaveRef = useRef(true)
  const saveGroupsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    getActiveInstances().then(({ ok, data }) => {
      if (ok) setInstanceList(data)
    })
  }, [])

  useEffect(() => {
    getInstancePresets().then(({ ok, data }) => {
      if (ok) {
        setInstancePresets(data)
      }
    })
  }, [])

  useEffect(() => {
    getPlaybackSettings().then(({ ok, data }) => {
      if (ok) {
        setPlaybackSettings(data)
      }
    })
  }, [])

  useEffect(() => {
    getChannelGroups().then(({ ok, data }) => {
      if (ok) setChannelGroups(data)
    })
  }, [])

  useEffect(() => {
    getPlaylistFilter().then(({ ok, data }) => {
      if (ok) {
        skipNextGroupsSaveRef.current = true
        setSelectedGroups(data.selectedGroups)
      }
    })
  }, [])

  useEffect(() => {
    if (skipNextGroupsSaveRef.current) {
      skipNextGroupsSaveRef.current = false
      return
    }

    if (saveGroupsTimeoutRef.current) clearTimeout(saveGroupsTimeoutRef.current)
    saveGroupsTimeoutRef.current = setTimeout(() => {
      savePlaylistFilter(selectedGroups)
    }, 800)

    return () => {
      if (saveGroupsTimeoutRef.current) clearTimeout(saveGroupsTimeoutRef.current)
    }
  }, [selectedGroups])

  function handleAddPresetTreeNode(field: keyof PlaybackSettings, parentKey: string | null) {
    modalInstanceViewRef?.current?.show(preset => {
      const updated = addPresetTreeNode(playbackSettings?.[field] ?? null, parentKey, preset.name)
      updatePlaybackSettings({ [field]: updated }).then(({ ok, data }) => {
        if (ok) setPlaybackSettings(data)
      })
    })
  }

  function handleEditPresetTreeNode(field: keyof PlaybackSettings, key: string) {
    modalInstanceViewRef?.current?.show(preset => {
      const updated = replacePresetTreeNode(playbackSettings?.[field] ?? null, key, preset.name)
      updatePlaybackSettings({ [field]: updated }).then(({ ok, data }) => {
        if (ok) setPlaybackSettings(data)
      })
    })
  }

  function handleRemovePresetTreeNode(field: keyof PlaybackSettings, key: string) {
    const updated = removePresetTreeNode(playbackSettings?.[field] ?? null, key)
    updatePlaybackSettings({ [field]: updated }).then(({ ok, data }) => {
      if (ok) setPlaybackSettings(data)
    })
  }

  useEffect(() => {
    refreshActiveInstances()
    const interval = setInterval(() => {
      refreshActiveInstances()
    }, 5000)

    return () => {
      clearInterval(interval)
    }
  }, [])

  function refreshActiveInstances() {
    getActiveInstances().then(({ ok, data }) => {
      if (ok) {
        setInstanceList(data)
      }
    })
  }

  useEffect(() => {
    if (instanceList.length && !videoSource && hlsToken) {
      const firstStreamable = getFirstStreamable(instanceList)

      if (firstStreamable) {
        setTimeout(() => {
          setVideoSource({
            type: firstStreamable.outputType,
            url:
              firstStreamable.outputType === SourceType.HLS
                ? './live/playlist.m3u8?token=' + hlsToken
                : firstStreamable.instanceName,
            videoCodec: firstStreamable.videoCodec,
            audioCodec: firstStreamable.audioCodec,
          })
        }, 1000)
      }
    }
  }, [instanceList, hlsToken])

  function getFirstStreamable(activeInstances: ActiveInstance[]): ActiveInstance | null {
    for (let i = 0; i < activeInstances.length; i++) {
      const isStreamable =
        activeInstances[i].outputType === SourceType.HLS ||
        (activeInstances[i].outputType === SourceType.WEBSOCKET && !isIOS)
      if (isStreamable) return activeInstances[i]
      if (activeInstances[i].children?.length)
        return getFirstStreamable(activeInstances[i].children as ActiveInstance[])
    }
    return null
  }

  useEffect(() => {
    refreshCaptureList()
    const interval = setInterval(() => {
      refreshCaptureList()
    }, 60 * 1000)

    return () => {
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    const fetchToken = async () => {
      const { ok, data } = await getHlsToken()
      if (ok) setHlsToken(data.token)
    }
    fetchToken()
  }, [])

  useEffect(() => {
    const handleDrawerState = (state: 'open' | 'closed' = 'open') => {
      if (drawerRef.current) animateDrawer(state)
    }

    if (ref)
      ref.current = {
        handleDrawerState,
      }
  }, [ref])

  const instanceLogLimit = 500

  useEffect(() => {
    if (!accessToken) return

    const unregister = registerWebsocketMessaging(message => {
      if (typeof message.data !== 'string') return

      if (message.data.startsWith('metadata:')) {
        const [, instanceName, duration, startDate] = message.data.split(':')
        setInstanceMetadata(prevState => {
          return {
            ...prevState,
            [instanceName]: { duration, startDate },
          }
        })
      } else if (message.data.startsWith('stderr:')) {
        // Manual slicing (not split(':')) because the log line itself
        // contains colons, e.g. "...time=00:00:10.00...".
        const rest = message.data.slice('stderr:'.length)
        const separatorIndex = rest.indexOf(':')
        const instanceName = rest.slice(0, separatorIndex)
        const line = rest.slice(separatorIndex + 1)

        setInstanceLogs(prevState => {
          const messages = [...(prevState[instanceName] ?? []), line]
          if (messages.length > instanceLogLimit) messages.shift()
          return { ...prevState, [instanceName]: messages }
        })
      }
    })
    return () => {
      unregister()
    }
  }, [accessToken, registerWebsocketMessaging])

  function subscribeToInstanceLog(instanceName: string) {
    setInstanceLogs(prevState => ({ ...prevState, [instanceName]: [] }))
    if (websocketOpen) webSocket.send('startlog:' + instanceName)
  }

  function unsubscribeFromInstanceLog(instanceName: string) {
    if (websocketOpen) webSocket.send('stoplog:' + instanceName)
    setInstanceLogs(prevState => {
      const { [instanceName]: _removed, ...rest } = prevState
      return rest
    })
  }

  const animationDuration = 200

  function animateDrawer(state: 'open' | 'closed' = 'open') {
    let startTime: number | null = null
    const drawerWidth = drawerRef.current!.offsetWidth

    const from = state === 'open' ? 0 : -drawerWidth
    const to = state === 'open' ? -drawerWidth : 0

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      const elapsedTime = timestamp - startTime
      const progress = Math.min(elapsedTime / animationDuration, 1)
      const easedProgress = 1 - Math.pow(1 - progress, 3)

      // Linear interpolation
      const current = from + (to - from) * easedProgress
      setDrawerPosition(current)

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }

  function refreshCaptureList() {
    getCaptureList().then(({ ok, data }) => {
      if (ok) {
        setCaptureList(data)
      }
    })
  }

  function handleSelectInstance(parentName: string | null) {
    currentSelectedParent.current = parentName
    setShowInstanceSelectModal(!showInstanceSelectModal)
    modalInstanceViewRef?.current?.show(preset => {
      startInstance({
        instancePreset: preset.name,
        assignToPreset: currentSelectedParent.current ?? undefined,
      }).then(({ ok, data }) => {
        if (ok) {
          setInstanceList(data)
          setShowInstanceSelectModal(!showInstanceSelectModal)
        }
      })
    })
  }

  function renderInstanceRow(item: ActiveInstance) {
    const statusColor = item.crashed ? '#e05d5d' : item.hasError ? '#ffb74d' : '#4caf50'
    const duration = item.seekable ? Number(instanceMetadata?.[item.instanceName]?.duration) : 0
    const currentTime = Number(item?.time ?? progressBarValue)
    const progressPercent = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0

    return (
      <>
        <ListRow
          label={item.instanceName}
          labelColor={statusColor}
          onClick={
            item.outputType !== SourceType.NONE
              ? () => {
                  setVideoSource({
                    type: item.outputType,
                    url:
                      item.outputType === SourceType.HLS
                        ? './live/playlist.m3u8?instanceName=' +
                          item.instanceName +
                          '&token=' +
                          hlsToken
                        : item.instanceName,
                    videoCodec: item.videoCodec,
                    audioCodec: item.audioCodec,
                  })
                }
              : undefined
          }
          actions={
            <>
              {item.outputType === SourceType.HLS ? (
                <ListRowAction
                  onClick={() => {
                    const type = 'text/plain'
                    const clipboardItemData = {
                      [type]:
                        window.location.href +
                        'live/playlist.m3u8' +
                        '?token=' +
                        hlsToken +
                        '&instanceName=' +
                        item.instanceName,
                    }
                    const clipboardItem = new ClipboardItem(
                      clipboardItemData as Record<string, string>
                    )
                    navigator.clipboard.write([clipboardItem])
                  }}
                >
                  <CopyIcon size={20} />
                </ListRowAction>
              ) : null}
              <ListRowAction
                onClick={() => modalInstanceLogViewRef?.current?.toggle(item.instanceName)}
              >
                <TerminalIcon
                  size={18}
                  color={item.hasError || item.crashed ? statusColor : undefined}
                />
              </ListRowAction>
              <ListRowAction
                onClick={() => {
                  stopInstance(item.instanceName).then(({ ok, data }) => {
                    if (ok) {
                      setInstanceList(data)
                    }
                  })
                }}
              >
                <StopIcon
                  size={20}
                  color='#e05d5d'
                />
              </ListRowAction>
            </>
          }
        />
        {item.seekable && duration > 0 ? (
          <div style={styles.seekBarContainer}>
            <input
              id='progress'
              className='seek-bar'
              type='range'
              min='0'
              max={duration}
              value={currentTime}
              style={{
                background: `linear-gradient(to right, #4caf50 ${progressPercent}%, #5b5b5b ${progressPercent}%)`,
              }}
              onChange={e => setProgressBarValue(e.target.value)}
              onPointerUp={() => {
                if (videoSource?.type === SourceType.WEBSOCKET && videoSource?.url) {
                  play({
                    instanceName: videoSource?.url,
                    options: {
                      timestamp: Number(progressBarValue),
                    },
                  })
                }
              }}
            />
            <div style={styles.seekBarTimeRow}>
              <span style={styles.seekBarTimeLabel}>{formatTime(currentTime)}</span>
              <span style={styles.seekBarTimeLabel}>{formatTime(duration)}</span>
            </div>
          </div>
        ) : null}
      </>
    )
  }

  function renderPresetTreeRow(field: keyof PlaybackSettings, node: PresetTreeNode) {
    return (
      <ListRow
        label={node.presetName}
        actions={
          <>
            <ListRowAction onClick={() => handleEditPresetTreeNode(field, node.presetName)}>
              <EditIcon size={18} />
            </ListRowAction>
            <ListRowAction onClick={() => handleRemovePresetTreeNode(field, node.presetName)}>
              <DeleteIcon
                size={20}
                color='#e05d5d'
              />
            </ListRowAction>
          </>
        }
      />
    )
  }

  const searchChannel = (input: string, groups: string[]) => {
    setSearchQuery(input)
    const searchId = ++searchIdRef.current
    loadingMoreRef.current = false
    searchChannels(input, groups).then(({ ok, data }) => {
      if (!ok || searchId !== searchIdRef.current) return
      setSearchResults(data)
      setHasMoreResults(data.length === CHANNEL_PAGE_SIZE)
    })
  }

  const loadMoreChannels = () => {
    if (!searchQuery || !hasMoreResults || loadingMoreRef.current) return
    loadingMoreRef.current = true
    const searchId = searchIdRef.current
    searchChannels(searchQuery, selectedGroups, searchResults.length).then(({ ok, data }) => {
      if (searchId !== searchIdRef.current) return
      loadingMoreRef.current = false
      if (!ok) return
      setSearchResults(previous => [...previous, ...data])
      setHasMoreResults(data.length === CHANNEL_PAGE_SIZE)
    })
  }

  useEffect(() => {
    if (searchQuery) searchChannel(searchQuery, selectedGroups)
  }, [selectedGroups])

  return (
    <div
      ref={drawerRef}
      onScroll={event => {
        if (activeWindow !== menuItems.channelList.id) return
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
        if (scrollHeight - scrollTop - clientHeight < 300) loadMoreChannels()
      }}
      style={{
        ...styles.container,
        height: window.innerHeight - 50,
        transform: 'translateX(' + drawerPosition + 'px)',
      }}
    >
      <div style={styles.menuRow}>
        {Object.keys(menuItems).map(item => {
          const Icon = menuItems[item as keyof typeof menuItems].icon

          return (
            <TouchableOpacity
              onClick={() => {
                setActiveWindow(item)
              }}
            >
              {Icon ? (
                <Icon
                  size={36}
                  color={activeWindow === item ? 'red' : 'white'}
                />
              ) : (
                <p style={activeWindow === item ? styles.activeLabel : undefined}>{item}</p>
              )}
            </TouchableOpacity>
          )
        })}
      </div>

      {activeWindow === menuItems.remote.id ? <Remote /> : null}

      {activeWindow === menuItems.instanceControl.id ? (
        <div style={styles.instanceControlContainer}>
          <div style={styles.instanceControlHeader}>
            <Text style={styles.sectionLabel}>LAUFENDE INSTANZEN</Text>
            <ListRowAction
              onClick={() =>
                modalInstanceViewRef?.current?.show(preset =>
                  modalEditInstanceViewRef?.current?.show(preset)
                )
              }
            >
              <EditIcon size={18} />
            </ListRowAction>
          </div>
          <Tree
            nodes={instanceList}
            getKey={item => item.instanceName}
            getChildren={item => item.children}
            renderNode={renderInstanceRow}
            onAddChild={handleSelectInstance}
          />

          {activeShares.length ? (
            <>
              <Text style={{ ...styles.sectionLabel, padding: '10px 12px 6px' }}>
                BILDSCHIRMFREIGABEN
              </Text>
              {activeShares.map(share => (
                <ListRow
                  key={share.shareId}
                  label={share.ownerUsername + "'s screen"}
                  onClick={() => setVideoSource({ type: SourceType.WEBRTC, url: share.shareId })}
                  actions={
                    <ListRowAction onClick={() => stopScreenShare(share.shareId)}>
                      <StopIcon
                        size={20}
                        color='#e05d5d'
                      />
                    </ListRowAction>
                  }
                />
              ))}
            </>
          ) : null}
        </div>
      ) : null}

      {activeWindow === menuItems.captureList.id ? (
        <div style={styles.fullWidth}>
          <Text style={{ ...styles.sectionLabel, padding: '10px 12px 6px' }}>
            {'ANSTEHENDE AUFNAHMEN: ' + captureList.length}
          </Text>
          {captureList.map((item, index) => (
            <ListRow
              key={item.fileName + index}
              leading={<CaptureIcon size={22} />}
              label={item.fileName}
              actions={
                <ListRowAction
                  onClick={() => {
                    deleteCaptureItem(index, 'ffmpegCaptureHD').then(({ ok, data }) => {
                      if (ok) {
                        setCaptureList(data)
                      }
                    })
                  }}
                >
                  <DeleteIcon
                    size={20}
                    color='#e05d5d'
                  />
                </ListRowAction>
              }
            />
          ))}
        </div>
      ) : null}

      {activeWindow === menuItems.channelList.id ? (
        <div style={styles.fullWidth}>
          <div style={styles.channelListHeader}>
            <Input
              style={styles.channelSearchInput}
              onChange={event => {
                searchChannel(event?.target.value, selectedGroups)
              }}
              placeholder={'Suche...'}
            />
            <TouchableOpacity
              style={styles.filterButton}
              onClick={() => modalGroupFilterViewRef?.current?.show()}
            >
              <Text style={selectedGroups.length ? styles.filterLabelActive : styles.filterLabel}>
                {'Filter' + (selectedGroups.length ? ` (${selectedGroups.length})` : '')}
              </Text>
            </TouchableOpacity>
          </div>
          <ChannelList
            searchResults={searchResults}
            accessToken={accessToken}
            onChannelItemClick={url => {
              getVideoToken().then(({ ok, data }) => {
                if (ok && data.videoToken) {
                  setVideoSource({
                    type: SourceType.FILE,
                    url: './video/forward?videoUrl=' + url + '&token=' + data.videoToken,
                  })
                } else {
                  window.location.href = './login'
                }
              })
            }}
          />
        </div>
      ) : null}

      {activeWindow === menuItems.vodChannel.id ? (
        <VodChannel instanceViewModal={modalInstanceViewRef} />
      ) : null}

      {activeWindow === menuItems.settings.id ? (
        <div style={{display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', width: '100%'}}>
          <div style={styles.fullWidth}>
            <Text style={{ ...styles.sectionLabel, padding: '10px 12px 6px' }}>
              STANDARD-PRESETS
            </Text>

            <Text style={{ ...styles.sectionLabel, padding: '0 12px 4px' }}>Livestream</Text>
            <Tree
              nodes={
                playbackSettings?.defaultLivePreset ? [playbackSettings.defaultLivePreset] : []
              }
              getKey={node => node.presetName}
              getChildren={node => node.children}
              renderNode={node => renderPresetTreeRow('defaultLivePreset', node)}
              onAddChild={parentKey => handleAddPresetTreeNode('defaultLivePreset', parentKey)}
            />

            <Text style={{ ...styles.sectionLabel, padding: '10px 12px 4px' }}>Video</Text>
            <Tree
              nodes={
                playbackSettings?.defaultVideoPreset ? [playbackSettings.defaultVideoPreset] : []
              }
              getKey={node => node.presetName}
              getChildren={node => node.children}
              renderNode={node => renderPresetTreeRow('defaultVideoPreset', node)}
              onAddChild={parentKey => handleAddPresetTreeNode('defaultVideoPreset', parentKey)}
            />
          </div>
          <LogoutButton />
        </div>
      ) : null}

      <InstanceViewModal
        ref={modalInstanceViewRef}
        instancePresets={instancePresets}
        onPresetsChange={setInstancePresets}
      />
      <EditInstanceViewModal ref={modalEditInstanceViewRef} />
      <GroupFilterViewModal
        ref={modalGroupFilterViewRef}
        groups={channelGroups}
        selectedGroups={selectedGroups}
        onSelectedGroupsChange={setSelectedGroups}
      />
      <InstanceLogViewModal
        ref={modalInstanceLogViewRef}
        logsByInstance={instanceLogs}
        onSubscribe={subscribeToInstanceLog}
        onUnsubscribe={unsubscribeFromInstanceLog}
      />
    </div>
  )
}

export default Drawer
